import React, { useEffect, useState } from 'react';
import { ThemeProvider, createTheme, CssBaseline, Container, Box, CircularProgress } from '@mui/material';
import { SnackbarProvider } from 'notistack';
import { Auth } from './components/Auth';
import { ChatRoom } from './components/ChatRoom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#3f51b5',
      light: '#757de8',
      dark: '#002984',
    },
    secondary: {
      main: '#f50057',
      light: '#ff4081',
      dark: '#c51162',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h6: {
      fontWeight: 500,
    },
    body1: {
      fontSize: '0.95rem',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
  },
});

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAuthenticated(!!user);
    });

    return () => unsubscribe();
  }, []);

  if (isAuthenticated === null) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.default',
          }}
        >
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SnackbarProvider
        maxSnack={3}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        autoHideDuration={3000}
      >
        <Box
          sx={{
            minHeight: '100vh',
            bgcolor: 'background.default',
            py: { xs: 0, sm: 2 },
            px: { xs: 0, sm: 2 },
          }}
        >
          <Container
            maxWidth="md"
            sx={{
              height: '100vh',
              p: { xs: 0, sm: 2 },
            }}
          >
            {!isAuthenticated ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                  paddingTop: { xs: '15vh', sm: '20vh' },
                  height: '100%',
                  p: { xs: 2, sm: 0 },
                }}
              >
                <Auth />
              </Box>
            ) : (
              <ChatRoom />
            )}
          </Container>
        </Box>
      </SnackbarProvider>
    </ThemeProvider>
  );
}

export default App;
